import { inject, Injectable } from '@angular/core';
import { ServiceInventoryService } from '@brightside-web/desktop/data-access/service-inventory';
import { filter, Observable, of, Subscription, tap, delay } from 'rxjs';
import { Store } from '@ngrx/store';
import {
  loadServiceNotifications, loadServiceNotificationsSuccess,
  patchServiceNotifications,
  selectAllServiceNotifications,
  selectHasUnreadNotifications,
  ServiceNotificationItem,
  ServiceNotificationType
} from '@brightside-web/desktop/feature/service-notifications';
import { map } from 'rxjs/operators';
import { MOCK_NOTIFICATIONS } from './store/service-notifications.mock';
import { Environment } from '@micro-core/environment';

@Injectable({
  providedIn: 'root'
})
export class ServiceNotificationsService {
  private _loaded = false;
  private _serviceInventory = inject(ServiceInventoryService)
  private _store = inject(Store);
  private _sub = new Subscription();
  private _env = inject(Environment);
  private _timer: any;

  constructor() {
    this._sub.add(
      this._serviceInventory.getService('notifications')
        .pipe(
          filter(service => !!service && !this._loaded),
          tap(service => {
            this._loaded = true;
            this._store.dispatch(loadServiceNotifications());
            if (!this._timer) {
              this._timer = setInterval( () => {
                  this._store.dispatch(loadServiceNotifications());
                }, 600000
              )
            }
          })
        )
        .subscribe()
    );
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
    clearInterval(this._timer);
  }

  getAllNotifications$(): Observable<ServiceNotificationItem[]> {
    return this._store.select(selectAllServiceNotifications);
  }

  getGroupedNotifications$(): Observable<{
    alerts: ServiceNotificationItem[];
    others: ServiceNotificationItem[];
    urgent: ServiceNotificationItem[];
  }> {
    return this.getAllNotifications$().pipe(
      map((notifications) => {
        const sortByUnreadThenDate = (a: ServiceNotificationItem, b: ServiceNotificationItem) => {
          if (a.read !== b.read) {
            return Number(a.read) - Number(b.read); // Unread (false = 0) comes before read (true = 1)
          }
          // If read status is the same, sort by createTs descending (most recent first)
          return new Date(b.createTs).getTime() - new Date(a.createTs).getTime();
        };

        const alerts = notifications
          .filter(n => n.type === ServiceNotificationType.ALERT_ACTION)
          .sort(sortByUnreadThenDate);

        const others = notifications
          .filter(n =>
            n.type === ServiceNotificationType.INFO ||
            n.type === ServiceNotificationType.INFO_ACTION
          )
          .sort(sortByUnreadThenDate);

        const urgent = notifications.filter(n =>
          n.type === ServiceNotificationType.URGENT
        ).sort(sortByUnreadThenDate);

        return { alerts, others, urgent };
      })
    );
  }


  hasUnreadNotifications$(): Observable<boolean> {
    return this._store.select(selectHasUnreadNotifications);
  }

  markAsReadById(id: string): void {
    this._patchNotifications([{ id, read: true }]);
  }

  markAsReadByItems(items: ServiceNotificationItem[]): void {
    const updates = items
      .filter(item => !item.read)
      .map(item => ({ id: item.id, read: true }));

    if (updates.length > 0) {
      this._patchNotifications(updates);
    }
  }


  mockTheNotifications(): void {
    if (!this._env.production) this._store.dispatch(loadServiceNotificationsSuccess({ notifications: MOCK_NOTIFICATIONS }));
  }

  private _patchNotifications(updates: { id: string; read: boolean }[]): void {
    this._store.dispatch(patchServiceNotifications({ updates }));
  }
}
